import {Box, useElementSize} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {isNotNil} from 'ramda';

import {getSafely, suffixTestId, TestIdProps} from 'shared/web';

import {MarketMetricsPosition} from '../utils/computeMarketMetrics';

interface PricePreviewSparklineProps extends TestIdProps {
  /** Advertised prices sorted ascending (the rising curve). */
  priceCurve: number[];
  /** Where the priced vehicle ranks among the advertised cars (recommended price). */
  position: MarketMetricsPosition | null;
  /** The vehicle's own current listing price, when it is already advertised. */
  advertisedPrice?: number | null;
}

/** Fallback height until the cell is measured; the chart then fills the section height. */
const DEFAULT_HEIGHT = 56;
/** The chart is 1.8× as wide as it is tall. */
const ASPECT_RATIO = 1.8;
const PADDING = 6;
const DOT_RADIUS = 3;
const MIN_POINTS = 2;
const FULL = 100;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Miniature preview of the advertised-price progression — the same shape as the
 * overview's actual-price chart: an orange rising curve of the sorted listing prices
 * with a dot marking where the priced vehicle sits among them. Fills its cell so it
 * lines up with the supply×demand section's height.
 */
export function PricePreviewSparkline(props: PricePreviewSparklineProps) {
  const theme = useTheme();
  const [ref, , containerHeight] = useElementSize<HTMLDivElement>();

  if (props.priceCurve.length < MIN_POINTS) {
    return null;
  }

  const lineColor = theme.colors.palettes.orange[60][100];
  const dotColor = theme.colors.text.primary;
  const dotStroke = theme.colors.palettes.white[10][100];
  // Same colour the main statistics chart paints the vehicle's own advertisement.
  const advertisedDotColor = theme.colors.palettes.green[50][100];
  const axisColor = theme.colors.palettes.neutral[40][100];

  const height = containerHeight ?? DEFAULT_HEIGHT;
  const width = Math.round(height * ASPECT_RATIO);
  const lastIndex = props.priceCurve.length - 1;
  const minPrice = getSafely(props.priceCurve, 0) ?? 0;
  const maxPrice = getSafely(props.priceCurve, lastIndex) ?? 0;
  const priceRange = maxPrice - minPrice || 1;
  const plotWidth = width - 2 * PADDING;
  const plotHeight = height - 2 * PADDING;

  const toX = (index: number): number => PADDING + (index / lastIndex) * plotWidth;
  // Higher price sits higher on the chart (top), like the overview price chart.
  const toY = (price: number): number =>
    PADDING + (1 - (price - minPrice) / priceRange) * plotHeight;

  const linePoints = props.priceCurve
    .map((price, index) => `${toX(index)},${toY(price)}`)
    .join(' ');

  const priceAtFraction = (fraction: number): number => {
    const exact = fraction * lastIndex;
    const lower = Math.floor(exact);
    const upper = Math.min(lastIndex, lower + 1);
    const lowerPrice = getSafely(props.priceCurve, lower) ?? 0;
    const upperPrice = getSafely(props.priceCurve, upper) ?? 0;
    return lowerPrice + (upperPrice - lowerPrice) * (exact - lower);
  };

  const dotFraction =
    isNotNil(props.position) && props.position.total > 0
      ? clamp(props.position.percent / FULL, 0, 1)
      : null;

  // Mint dot: the vehicle's own listing price mapped onto the curve — only when it is
  // already advertised. Its x is the price's rank; its y is the price itself.
  const advertisedPrice = props.advertisedPrice;
  const advertisedFraction = isNotNil(advertisedPrice)
    ? clamp(props.priceCurve.filter((price) => price < advertisedPrice).length / lastIndex, 0, 1)
    : null;

  return (
    <Box ref={ref} height="100%" backgroundColor="palettes.neutral.10.100" borderRadius="small">
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        data-testid={suffixTestId('pricePreviewSparkline', props)}
      >
        {/* Bare X and Y axes (no ticks or labels) — purely a visual frame */}
        <line
          x1={PADDING}
          y1={PADDING}
          x2={PADDING}
          y2={height - PADDING}
          stroke={axisColor}
          strokeWidth={1}
        />
        <line
          x1={PADDING}
          y1={height - PADDING}
          x2={width - PADDING}
          y2={height - PADDING}
          stroke={axisColor}
          strokeWidth={1}
        />
        <polyline
          points={linePoints}
          fill="none"
          stroke={lineColor}
          strokeWidth={1.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {isNotNil(advertisedFraction) && isNotNil(advertisedPrice) ? (
          <circle
            cx={PADDING + advertisedFraction * plotWidth}
            cy={toY(clamp(advertisedPrice, minPrice, maxPrice))}
            r={DOT_RADIUS}
            fill={advertisedDotColor}
            stroke={dotStroke}
            strokeWidth={1}
          />
        ) : null}
        {isNotNil(dotFraction) ? (
          <circle
            cx={PADDING + dotFraction * plotWidth}
            cy={toY(priceAtFraction(dotFraction))}
            r={DOT_RADIUS}
            fill={dotColor}
            stroke={dotStroke}
            strokeWidth={1}
          />
        ) : null}
      </svg>
    </Box>
  );
}
